
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { WorkoutExercise } from '@/types/workout';

/**
 * Checks if a workout has already been completed today for a user
 */
export const checkCompletedWorkoutToday = async (userId: string) => {
  const today = new Date().toISOString().split('T')[0];
  
  console.log('Checking for existing completed workouts today...');
  const { data: existingCompletedWorkouts, error } = await supabase
    .from('workouts')
    .select('id')
    .eq('user_id', userId)
    .eq('date_assigned', today)
    .eq('completed', true);
  
  if (error) {
    console.error('Error checking existing completed workouts:', error);
    throw error;
  }
  
  return { 
    hasCompletedToday: existingCompletedWorkouts && existingCompletedWorkouts.length > 0,
    completedWorkouts: existingCompletedWorkouts
  };
};

/**
 * Gets or creates a workout for today
 */
export const getOrCreateTodaysWorkout = async (userId: string) => {
  const today = new Date().toISOString().split('T')[0];
  
  console.log('Checking for any workouts assigned today...');
  const { data: todaysWorkouts, error } = await supabase
    .from('workouts')
    .select('id')
    .eq('user_id', userId)
    .eq('date_assigned', today);
    
  if (error) {
    console.error('Error fetching today\'s workouts:', error);
    throw error;
  }
  
  if (todaysWorkouts && todaysWorkouts.length > 0) {
    return { id: todaysWorkouts[0].id, isNew: false };
  }
  
  // Create new workout if none exists
  const { data: newWorkout, error: insertError } = await supabase
    .from('workouts')
    .insert({
      user_id: userId,
      title: 'Daily AI Workout',
      date_assigned: today,
      date_completed: null,
      completed: false,
      description: 'Interactive AI training exercises'
    })
    .select('id')
    .single();
  
  if (insertError) {
    console.error('Error creating workout record:', insertError);
    throw insertError;
  }
  
  return { id: newWorkout.id, isNew: true };
};

/**
 * Marks a workout as completed
 */
export const completeWorkout = async (workoutId: string, userId: string) => {
  const { error } = await supabase
    .from('workouts')
    .update({
      completed: true,
      date_completed: new Date().toISOString(),
      description: 'Completed interactive AI training exercises'
    })
    .eq('id', workoutId)
    .eq('user_id', userId);
    
  if (error) {
    console.error('Error updating workout completion:', error);
    throw error;
  }
  
  return true;
};

/**
 * Records a user activity
 */
export const recordUserActivity = async (userId: string, activityType: string, title: string, points: number) => {
  const { error } = await supabase
    .from('user_activities')
    .insert({
      user_id: userId,
      activity_type: activityType,
      title: title,
      date: new Date().toISOString(),
      completed: true,
      points: points
    });
  
  if (error) {
    console.error('Error recording activity:', error);
    throw error;
  }
  
  return true;
};

/**
 * Request AI feedback for a workout exercise
 */
export const requestExerciseFeedback = async (exerciseTitle: string, exerciseDescription: string, userAnswer: string) => {
  const { data, error } = await supabase.functions.invoke('provide-exercise-feedback', {
    body: {
      exerciseTitle,
      exerciseDescription,
      userAnswer
    }
  });
  
  if (error) throw error;
  
  return data.feedback || '';
};

/**
 * Default workout exercises
 */
export const getDefaultWorkoutExercises = (): WorkoutExercise[] => {
  return [
    {
      id: 1,
      title: "AI Agent Use Case",
      description: "Identify a specific use case where an AI agent could improve your department's efficiency.",
      type: "writing",
      timeEstimate: "5 min",
      tips: "Consider repetitive tasks that could be automated or areas where decision-making could be enhanced with AI assistance.",
    },
    {
      id: 2,
      title: "Prompt Engineering",
      description: "Write an effective prompt to get a competitive analysis of your industry from an AI assistant.",
      type: "writing",
      timeEstimate: "5 min",
      tips: "Be specific about the aspects you want analyzed and the format in which you want the information presented.",
    },
    {
      id: 3,
      title: "Risk Assessment",
      description: "Identify three potential risks of implementing AI in your customer service operations.",
      type: "writing",
      timeEstimate: "5 min",
      tips: "Consider data privacy, quality of responses, and human/AI collaboration challenges.",
    },
    {
      id: 4,
      title: "Strategic Implementation",
      description: "Outline a brief plan for introducing an AI tool to your team, focusing on gaining buy-in and addressing concerns.",
      type: "writing",
      timeEstimate: "5 min",
      tips: "Include training, communication strategy, and how you'll measure success.",
    }
  ];
};
